/**
 * Integrations API Layer.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  Integration,
  IntegrationCreate,
  ListAssetRequest,
  Listing,
} from "@/types/integration";

export function useIntegrations() {
  return useQuery<Integration[]>({
    queryKey: ["integrations"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/integrations")).data as Integration[],
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation<Integration, Error, IntegrationCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/integrations", data)).data as Integration,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/api/v1/integrations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
}

export function useAssetListings(assetId: string | undefined) {
  return useQuery<Listing[]>({
    queryKey: ["listings", assetId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/integrations/assets/${assetId}/listings`))
        .data as Listing[],
    enabled: !!assetId,
  });
}

export function useListAsset(assetId: string) {
  const qc = useQueryClient();
  return useMutation<Listing, Error, ListAssetRequest>({
    mutationFn: async (data) =>
      (
        await apiClient.post(
          `/api/v1/integrations/assets/${assetId}/list`,
          data,
        )
      ).data as Listing,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["listings", assetId] }),
  });
}

export function useDelistAsset(assetId: string) {
  const qc = useQueryClient();
  return useMutation<Listing, Error, string>({
    mutationFn: async (listingId) =>
      (
        await apiClient.post(
          `/api/v1/integrations/listings/${listingId}/delist`,
        )
      ).data as Listing,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["listings", assetId] }),
  });
}
